/**
 * Inline portal-access row rendered inside each contact card.
 * Mirrors the visual rhythm of Email/Phone rows (icon + label + value)
 * and adds an action button on the right that reflects current state.
 */
import { useState } from "react"
import type { SxProps, Theme } from "@mui/material/styles"
import { Box, Button, CircularProgress, Typography } from "@mui/material"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  KeyRound,
  Mail,
  ShieldCheck,
  ShieldOff,
  RefreshCw,
  CircleDashed,
} from "lucide-react"
import {
  grantPortalAccess,
  revokePortalAccess,
  resendPortalInvitation,
  type PortalAccessState,
} from "@crm-src/lib/support-sync-api"
import { handleApiError } from "@crm-src/lib/error-handler"

export type ContactPortalState = PortalAccessState | { access_state: "none" } | null

interface Props {
  contactId: number | string
  state: ContactPortalState
  onChanged: () => void
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return ""
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 60) return `${Math.floor(diff)}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

export function ContactPortalAccessButton({ contactId, state, onChanged }: Props) {
  const { toast } = useToast()
  const [busy, setBusy] = useState(false)

  if (state === null) return null  // loading or non-admin → render nothing

  const accessState = state.access_state
  const fullState = state as PortalAccessState

  async function run<T>(fn: () => Promise<T>, label: string) {
    setBusy(true)
    try {
      await fn(); toast({ title: label }); onChanged()
    } catch (e) {
      toast({ title: "Action failed", description: handleApiError(e), variant: "destructive" })
    } finally {
      setBusy(false)
    }
  }

  // ── Per-state visual config ──────────────────────────────────────
  let icon: React.ReactNode
  let valueText: string
  let valueSx: SxProps<Theme> = { fontSize: 12, color: "text.secondary" }
  let actions: React.ReactNode = null

  switch (accessState) {
    case "active":
      icon = <Box sx={{ display: "flex", color: "success.main" }}><ShieldCheck size={16} /></Box>
      valueText = fullState.accepted_at
        ? `Active — accepted ${timeAgo(fullState.accepted_at)}`
        : "Active"
      valueSx = { fontSize: 12, color: "success.dark", fontWeight: 500 }
      actions = (
        <Button size="small" variant="outlined" color="error"
                onClick={() => run(() => revokePortalAccess(contactId), "Access revoked")}
                disabled={busy}
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <ShieldOff size={14} />}>
          Revoke access
        </Button>
      )
      break

    case "invited":
      icon = <Box sx={{ display: "flex", color: "warning.main" }}><Mail size={16} /></Box>
      valueText = fullState.invited_at
        ? `Invitation sent ${timeAgo(fullState.invited_at)} — awaiting acceptance`
        : "Invitation sent — awaiting acceptance"
      valueSx = { fontSize: 12, color: "warning.dark" }
      actions = (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button size="small" variant="outlined" color="inherit"
                  onClick={() => run(() => resendPortalInvitation(contactId), "Invitation re-sent")}
                  disabled={busy}
                  startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}>
            Resend
          </Button>
          <Button size="small" variant="outlined" color="error"
                  onClick={() => run(() => revokePortalAccess(contactId), "Invitation cancelled")}
                  disabled={busy}
                  startIcon={<ShieldOff size={14} />}>
            Cancel
          </Button>
        </Box>
      )
      break

    case "revoked":
      icon = <Box sx={{ display: "flex", color: "error.main" }}><ShieldOff size={16} /></Box>
      valueText = fullState.revoked_at
        ? `Revoked ${timeAgo(fullState.revoked_at)}`
        : "Revoked"
      valueSx = { fontSize: 12, color: "error.dark" }
      actions = (
        <Button size="small" variant="outlined" color="inherit"
                onClick={() => run(() => grantPortalAccess(contactId), "Invitation queued")}
                disabled={busy}
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <KeyRound size={14} />}>
          Re-grant access
        </Button>
      )
      break

    case "none":
    default:
      icon = <Box sx={{ display: "flex", color: "text.disabled" }}><CircleDashed size={16} /></Box>
      valueText = "No access"
      valueSx = { fontSize: 12, color: "text.secondary", fontStyle: "italic" }
      actions = (
        <Button size="small" variant="contained"
                onClick={() => run(() => grantPortalAccess(contactId), "Invitation queued")}
                disabled={busy}
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <KeyRound size={14} />}>
          Grant access
        </Button>
      )
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
      <Box sx={{ flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>Customer Support Portal</Typography>
        <Typography sx={valueSx}>{valueText}</Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>{actions}</Box>
    </Box>
  )
}
